import React, { useContext } from 'react';

import Typography from '@mui/material/Typography';
import cx from 'classnames';

import PlaySmallIcon from '../../assets/icons/PlaySmallIcon';
import { UserInfoCtx } from '../../containers/CommonLayout/CommonLayout';
import Modal, { Modals } from '../../modals/Modal';
import { BREAKPOINTS_VALUES, colorsPalette } from '../../theme';
import { createCategoryLink } from '../../utils/helpers';
import useToggleFromWatchlist from '../../utils/hooks/useToggleFromWatchlist';
import { CardDurationInfo } from '../Card';
import { ConditionalLink } from '../ConditionalLink';
import { LazyLoadImage } from '../LazyloadImage';
import ToggleFromList from '../ToggleFromList/ToggleFromList';
import { CategoryLabel } from '../UI/CategoryLabel';
import DifficultyIcon from '../UI/DifficultyIcon';
import { ProgressLine } from '../UI/ProgressLine';
import { VideoInterface } from './Video';
import { landscapeStyles } from './styles';

export const LandScapeVideo: React.FC<VideoInterface> = ({
  id,
  title,
  duration,
  userName,
  categories,
  difficulty,
  previewUrl,
  description,
  videoWidth,
  background,
  progress,
  handleClick,
  durationNumb,
  link,
  expertLink,
  verseId,
  bumperUrl,
  isInWatchlist,
  shouldOpenInModal = false,
}) => {
  const { userInfo } = useContext(UserInfoCtx);
  const memoizedStylesFunc = React.useMemo(
    () => landscapeStyles(background, videoWidth),
    [background, videoWidth],
  );
  const classes = memoizedStylesFunc();
  const categoryName = categories && Array.isArray(categories) ? categories[0]?.title : null;
  const categoryLink = createCategoryLink(categories?.[0]);

  const [toggleFromWatchlist, toggleFromWatchlistLoading] = useToggleFromWatchlist(
    isInWatchlist,
    id,
  );

  return (
    <Modal
      verseId={verseId}
      duration={durationNumb}
      title={title}
      expertName={userName}
      bumperUrl={bumperUrl}
    >
      {({ openModal }) => {
        const playVideo = () => {
          if (shouldOpenInModal) {
            openModal(Modals.VideoModal);
            return;
          }

          if (handleClick || link) {
            handleClick && handleClick(id);
          }
        };

        return (
          <div className={classes.videoWrap}>
            <ConditionalLink withLink={link && !shouldOpenInModal} to={link}>
              <div className={cx(classes.imgWrap, 'video-preview-wrapper')} onClick={playVideo}>
                <LazyLoadImage
                  className={classes.videoImg}
                  path={previewUrl}
                  alt={title}
                  draggable={false}
                  srcset={[{ width: 420 }, { width: 370 }, { width: 350 }, { width: 340 }]}
                  sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 420px, (max-width: ${BREAKPOINTS_VALUES['md']}) 370px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 350px, 340px`}
                  imageWrapperClass={classes.lazyWrapper}
                  objectFitPolyfill="cover"
                />

                <div className={classes.btnWrap}>
                  <PlaySmallIcon className={classes.btn} color={colorsPalette.white} />
                </div>
              </div>
            </ConditionalLink>
            <div className={classes.catDifWrap}>
              {categoryName && (
                <CategoryLabel
                  url={categoryLink}
                  name={categoryName}
                  wrapperClass={classes.category}
                />
              )}
              <DifficultyIcon difficulty={difficulty} />
            </div>

            <div className={classes.textWrap}>
              <ConditionalLink withLink={link && !shouldOpenInModal} to={link}>
                <Typography
                  variant="h3"
                  component="h3"
                  className={classes.title}
                  onClick={playVideo}
                >
                  {title}
                </Typography>
                <Typography className={classes.description}>{description}</Typography>
              </ConditionalLink>
              <div className={classes.infoRow}>
                {(userName || duration) && (
                  <CardDurationInfo durationName={userName} duration={duration} link={expertLink} />
                )}
                {userInfo && (
                  <ToggleFromList
                    disabled={toggleFromWatchlistLoading}
                    isAdd={!isInWatchlist}
                    onClick={toggleFromWatchlist}
                    noBackground
                  />
                )}
              </div>
              {(progress || progress === 0) && <ProgressLine width={progress} />}
            </div>
          </div>
        );
      }}
    </Modal>
  );
};

export default LandScapeVideo;
